'use client';

import Link from 'next/link';
import type { IconSvgElement } from '@hugeicons/react';
import { ArrowDown01Icon, Logout01Icon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import { Logo } from '../brand/logo';
import { LoamifyIcon } from '../icons/loamify-icon';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { Badge } from '../ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from '../ui/sidebar';
import { DefaultAvatar } from '../patterns/default-avatar';

export type ConsoleNavItem = {
  label: string;
  href: string;
  icon: IconSvgElement;
  badge?: string;
};

export type ConsoleNavGroup = {
  label: string;
  items: ConsoleNavItem[];
};

export type ConsoleSidebarBrand = {
  title: string;
  subtitle?: string;
  href: string;
  mark?: string;
};

export type ConsoleSidebarUser = {
  name: string;
  role?: string;
  avatarUrl?: string;
};

export type ConsoleUserMenuItem = {
  label: string;
  href?: string;
  external?: boolean;
  onClick?: () => void;
};

export interface ConsoleSidebarProps {
  brand: ConsoleSidebarBrand;
  logo?: React.ReactNode;
  topItems?: ConsoleNavItem[];
  groups: ConsoleNavGroup[];
  activeHref?: string;
  user?: ConsoleSidebarUser;
  userMenuItems?: ConsoleUserMenuItem[];
  onLogout?: () => void;
  showBrand?: boolean;
  sidebarFooter?: React.ReactNode;
  onNavigate?: () => void;
  className?: string;
}

export function isConsoleNavActive(pathname: string, href: string) {
  return pathname === href || (href !== '/' && pathname.startsWith(`${href}/`));
}

function ConsoleNavLink({
  item,
  active,
  onNavigate,
}: {
  item: ConsoleNavItem;
  active: boolean;
  onNavigate?: () => void;
}) {
  return (
    <SidebarMenuItem>
      <SidebarMenuButton asChild isActive={active} tooltip={item.label}>
        <Link href={item.href} onClick={onNavigate}>
          <LoamifyIcon icon={item.icon} active={active} size={20} strokeWidth={1.75} />
          <span>{item.label}</span>
          {item.badge ? (
            <Badge variant="secondary" className="ml-auto">
              {item.badge}
            </Badge>
          ) : null}
        </Link>
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
}

function ConsoleSidebarUserMenu({
  user,
  userMenuItems = [],
  onLogout,
}: {
  user: ConsoleSidebarUser;
  userMenuItems?: ConsoleUserMenuItem[];
  onLogout?: () => void;
}) {
  const { isMobile } = useSidebar();

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
            >
              <Avatar className="size-8 shrink-0 rounded-lg">
                {user.avatarUrl ? <AvatarImage src={user.avatarUrl} alt={user.name} /> : null}
                <AvatarFallback className="rounded-lg p-0">
                  <DefaultAvatar
                    name={user.name}
                    seed={user.role ?? user.name}
                    textClassName="text-[10px]"
                  />
                </AvatarFallback>
              </Avatar>
              <div className="grid min-w-0 flex-1 text-left text-body-sm leading-tight">
                <span className="truncate font-medium">{user.name}</span>
                {user.role ? (
                  <span className="truncate text-caption text-muted-foreground">{user.role}</span>
                ) : null}
              </div>
              <HugeiconsIcon
                icon={ArrowDown01Icon}
                size={16}
                strokeWidth={1.75}
                className="ml-auto shrink-0 opacity-70"
              />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            side={isMobile ? 'bottom' : 'right'}
            align="end"
            className="w-56 rounded-lg"
            sideOffset={4}
          >
            {userMenuItems.map((item) =>
              item.href ? (
                <DropdownMenuItem key={item.label} asChild>
                  <Link
                    href={item.href}
                    target={item.external ? '_blank' : undefined}
                    rel={item.external ? 'noreferrer' : undefined}
                  >
                    {item.label}
                  </Link>
                </DropdownMenuItem>
              ) : (
                <DropdownMenuItem key={item.label} onClick={item.onClick}>
                  {item.label}
                </DropdownMenuItem>
              ),
            )}
            {userMenuItems.length > 0 && onLogout ? <DropdownMenuSeparator /> : null}
            {onLogout ? (
              <DropdownMenuItem
                onClick={onLogout}
                className="gap-2 text-destructive focus:text-destructive"
              >
                <HugeiconsIcon icon={Logout01Icon} size={16} strokeWidth={1.75} />
                Log out
              </DropdownMenuItem>
            ) : null}
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}

function ConsoleSidebarBrandBlock({
  brand,
  logo,
}: {
  brand: ConsoleSidebarBrand;
  logo?: React.ReactNode;
}) {
  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <SidebarMenuButton
          size="lg"
          asChild
          className="hover:bg-transparent active:bg-transparent group-data-[collapsible=icon]:!size-8 group-data-[collapsible=icon]:!p-0"
        >
          <Link href={brand.href} className="flex min-w-0 items-center">
            {logo ?? (
              <>
                <Logo
                  variant="full"
                  height={32}
                  href=""
                  className="group-data-[collapsible=icon]:hidden"
                />
                <Logo
                  variant="icon"
                  height={28}
                  href=""
                  className="hidden group-data-[collapsible=icon]:inline-flex"
                />
              </>
            )}
          </Link>
        </SidebarMenuButton>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}

export function ConsoleSidebar({
  brand,
  logo,
  topItems = [],
  groups,
  activeHref,
  user,
  userMenuItems,
  onLogout,
  showBrand = true,
  sidebarFooter,
  onNavigate,
  className,
}: ConsoleSidebarProps) {
  const pathname = activeHref ?? '';

  return (
    <Sidebar collapsible="icon" className={className}>
      {showBrand ? (
        <SidebarHeader>
          <ConsoleSidebarBrandBlock brand={brand} logo={logo} />
        </SidebarHeader>
      ) : null}

      <SidebarContent>
        {topItems.length > 0 ? (
          <SidebarGroup>
            <SidebarMenu>
              {topItems.map((item) => (
                <ConsoleNavLink
                  key={item.href}
                  item={item}
                  active={isConsoleNavActive(pathname, item.href)}
                  onNavigate={onNavigate}
                />
              ))}
            </SidebarMenu>
          </SidebarGroup>
        ) : null}

        {groups.map((group) => (
          <SidebarGroup key={group.label}>
            <SidebarGroupLabel>{group.label}</SidebarGroupLabel>
            <SidebarMenu>
              {group.items.map((item) => (
                <ConsoleNavLink
                  key={item.href}
                  item={item}
                  active={isConsoleNavActive(pathname, item.href)}
                  onNavigate={onNavigate}
                />
              ))}
            </SidebarMenu>
          </SidebarGroup>
        ))}
      </SidebarContent>

      <SidebarFooter>
        {sidebarFooter ? (
          <div className="group-data-[collapsible=icon]:hidden">{sidebarFooter}</div>
        ) : null}
        {user ? (
          <ConsoleSidebarUserMenu
            user={user}
            userMenuItems={userMenuItems}
            onLogout={onLogout}
          />
        ) : null}
      </SidebarFooter>
    </Sidebar>
  );
}
